import { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { CircleCheck, RotateCcw, ArrowLeft, RefreshCw, Loader2, CheckCheck } from 'lucide-react';
import { Header, ProgressBar, RoomSection, Confetti } from '@/components';
import {
  fetchPropertyWithChecklist,
  completeCleaning,
  getCompletedItems,
  toggleItemCompletion,
  type RoomWithItems,
} from '@/services';
import styles from './Checklist.module.css';

type LoadingState = 'loading' | 'error' | 'success';

interface LocationState {
  cleaningId: string;
  propertyName: string;
  propertyAddress: string;
  thumbnailUrl?: string | null;
  beds?: number | null;
  baths?: number | null;
  sqft?: number | null;
}

export default function ChecklistPage() {
  const navigate = useNavigate();
  const { propertyId } = useParams<{ propertyId: string }>();
  const location = useLocation();
  const state = location.state as LocationState | null;

  const cleaningId = state?.cleaningId || '';
  const propertyName = state?.propertyName || 'Property';
  const propertyAddress = state?.propertyAddress || '';
  const thumbnailUrl = state?.thumbnailUrl;
  const beds = state?.beds;
  const baths = state?.baths;
  const sqft = state?.sqft;

  const [rooms, setRooms] = useState<RoomWithItems[]>([]);
  const [completedItemIds, setCompletedItemIds] = useState<Set<string>>(new Set());
  const [loadingState, setLoadingState] = useState<LoadingState>('loading');
  const [error, setError] = useState<string | null>(null);
  const [showConfetti, setShowConfetti] = useState(false);
  const [isCompleting, setIsCompleting] = useState(false);

  const loadChecklist = useCallback(async () => {
    if (!propertyId || !cleaningId) {
      setError('Missing property or cleaning information');
      setLoadingState('error');
      return;
    }

    setLoadingState('loading');
    setError(null);

    const [checklistResult, completedResult] = await Promise.all([
      fetchPropertyWithChecklist(propertyId),
      getCompletedItems(cleaningId),
    ]);

    if (checklistResult.error) {
      setError(checklistResult.error.message);
      setLoadingState('error');
      return;
    }

    if (completedResult.error) {
      setError(completedResult.error.message);
      setLoadingState('error');
      return;
    }

    setRooms(checklistResult.data);
    setCompletedItemIds(new Set(completedResult.data));
    setLoadingState('success');
  }, [propertyId, cleaningId]);

  useEffect(() => {
    loadChecklist();
  }, [loadChecklist]);

  const { completed, total } = useMemo(() => {
    let totalCount = 0;
    let completedCount = 0;
    rooms.forEach((room) => {
      room.checklist_items.forEach((item) => {
        totalCount++;
        if (completedItemIds.has(item.id)) {
          completedCount++;
        }
      });
    });
    return { completed: completedCount, total: totalCount };
  }, [rooms, completedItemIds]);

  const isAllComplete = completed === total && total > 0;

  const handleToggleItem = useCallback((itemId: string) => {
    if (!cleaningId) return;

    setCompletedItemIds((prev) => {
      const wasCompleted = prev.has(itemId);
      const next = new Set(prev);
      
      if (wasCompleted) {
        next.delete(itemId);
      } else {
        next.add(itemId);
      }
      
      // Fire and forget the API call
      toggleItemCompletion(cleaningId, itemId, wasCompleted);
      
      return next;
    });
  }, [cleaningId]);

  const handleReset = useCallback(() => {
    setCompletedItemIds(new Set());
  }, []);

  const handleCheckAll = useCallback(() => {
    const allItemIds = new Set<string>();
    rooms.forEach((room) => {
      room.checklist_items.forEach((item) => {
        // Check all task items (video items still require uploading)
        if (item.type !== 'video') {
          allItemIds.add(item.id);
        }
      });
    });
    setCompletedItemIds(allItemIds);
  }, [rooms]);

  const handleComplete = useCallback(async () => {
    if (!cleaningId) return;

    setIsCompleting(true);
    const result = await completeCleaning(cleaningId);

    if (result.error) {
      setError(result.error.message);
      setIsCompleting(false);
      return;
    }

    setIsCompleting(false);
    setShowConfetti(true);
  }, [cleaningId]);

  const handleConfettiComplete = useCallback(() => {
    setShowConfetti(false);
    navigate('/properties');
  }, [navigate]);

  const handleBack = useCallback(() => {
    navigate('/properties');
  }, [navigate]);

  const roomsWithCompletion = useMemo(() => {
    return rooms.map((room) => {
      return {
        id: room.id,
        name: room.name,
        icon: room.icon || 'CheckCircle',
        items: room.checklist_items.map((item) => ({
          id: item.id,
          task: item.task,
          completed: completedItemIds.has(item.id),
          type: item.type as 'task' | 'video',
        })),
        photos: room.room_photos.map((photo) => ({
          id: photo.id,
          url: photo.url,
          caption: photo.caption || undefined,
        })),
      };
    });
  }, [rooms, completedItemIds]);

  if (loadingState === 'loading') {
    return (
      <div className={styles.page}>
        <div className={styles.centerContainer}>
          <Loader2 size={32} className={styles.spinner} />
          <p className={styles.loadingText}>Loading checklist...</p>
        </div>
      </div>
    );
  }

  if (loadingState === 'error') {
    return (
      <div className={styles.page}>
        <div className={styles.centerContainer}>
          <p className={styles.errorText}>{error}</p>
          <button className={styles.retryButton} onClick={loadChecklist}>
            <RefreshCw size={18} />
            <span>Try Again</span>
          </button>
          <button className={styles.backLink} onClick={handleBack}>
            <ArrowLeft size={18} />
            <span>Back to Properties</span>
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <Header
        propertyName={propertyName}
        propertyAddress={propertyAddress}
        thumbnailUrl={thumbnailUrl}
        beds={beds}
        baths={baths}
        sqft={sqft}
        onBack={handleBack}
      />
      <ProgressBar completed={completed} total={total} />

      <main className={styles.scrollContent}>
        {roomsWithCompletion.map((room) => (
          <RoomSection
            key={room.id}
            id={room.id}
            name={room.name}
            icon={room.icon}
            items={room.items}
            photos={room.photos}
            onToggleItem={handleToggleItem}
            defaultExpanded={false}
            cleaningId={cleaningId}
          />
        ))}

        <div className={styles.actionContainer}>
          {isAllComplete ? (
            <button
              className={`${styles.completeButton} ${isCompleting ? styles.buttonDisabled : ''}`}
              onClick={handleComplete}
              disabled={isCompleting}
            >
              {isCompleting ? (
                <Loader2 size={20} className={styles.spinner} />
              ) : (
                <CircleCheck size={20} />
              )}
              <span>{isCompleting ? 'Completing...' : 'Complete Cleaning'}</span>
            </button>
          ) : (
            <div className={styles.buttonRow}>
              <button className={styles.resetButton} onClick={handleReset}>
                <RotateCcw size={18} />
                <span>Reset</span>
              </button>
              <button className={styles.checkAllButton} onClick={handleCheckAll}>
                <CheckCheck size={18} />
                <span>Check All</span>
              </button>
            </div>
          )}
        </div>
      </main>

      {showConfetti && <Confetti onComplete={handleConfettiComplete} />}
    </div>
  );
}

